/**
 * Authentication Context for Re:Nav-96
 *
 * Provides authentication state and methods throughout the application.
 * Handles login, logout, registration, and automatic token refresh.
 *
 * @example
 * // In a component
 * import { useAuth } from '@/hooks/useAuth';
 *
 * function MyComponent() {
 *   const { user, isAuthenticated, login, logout } = useAuth();
 *   // ...
 * }
 */

import {
  createContext,
  useCallback,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type {
  User,
  AuthState,
  LoginCredentials,
  RegisterData,
} from '@/types';

import {
  authApi,
  hasToken,
  clearTokens,
  ApiClientError,
} from '@/services/api';

// ============================================================================
// Types
// ============================================================================

/**
 * Authentication context value interface
 */
export interface AuthContextValue extends AuthState {
  /**
   * Login with email and password
   */
  login: (credentials: LoginCredentials) => Promise<void>;

  /**
   * Register a new account
   */
  register: (data: RegisterData) => Promise<void>;

  /**
   * Logout and clear session
   */
  logout: () => Promise<void>;

  /**
   * Refresh the current user data
   */
  refreshUser: () => Promise<void>;

  /**
   * Error from the last auth operation
   */
  error: string | null;

  /**
   * Clear the current error
   */
  clearError: () => void;
}

// ============================================================================
// Context
// ============================================================================

/**
 * Authentication context
 * Use the useAuth hook instead of consuming this directly
 */
export const AuthContext = createContext<AuthContextValue | null>(null);

// ============================================================================
// Provider
// ============================================================================

interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider component
 *
 * Wraps the application and provides authentication state and methods.
 * Automatically attempts to restore session on mount if token exists.
 *
 * @example
 * // In App.tsx
 * import { AuthProvider } from '@/contexts/AuthContext';
 *
 * function App() {
 *   return (
 *     <AuthProvider>
 *       <RouterProvider router={router} />
 *     </AuthProvider>
 *   );
 * }
 */
export function AuthProvider({ children }: AuthProviderProps) {
  // Auth state
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Derived state
  const isAuthenticated = useMemo(() => !!user, [user]);
  const token = useMemo(() => (hasToken() ? 'exists' : null), [user]);

  /**
   * Clear the current error
   */
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  /**
   * Fetch current user data from API
   */
  const fetchUser = useCallback(async (): Promise<User | null> => {
    try {
      const userData = await authApi.getMe();
      return userData;
    } catch (err) {
      // If fetching user fails, clear tokens
      if (err instanceof ApiClientError && err.status === 401) {
        clearTokens();
      }
      return null;
    }
  }, []);

  /**
   * Refresh the current user data
   */
  const refreshUser = useCallback(async () => {
    if (!hasToken()) {
      setUser(null);
      return;
    }

    setIsLoading(true);
    try {
      const userData = await fetchUser();
      setUser(userData);
    } finally {
      setIsLoading(false);
    }
  }, [fetchUser]);

  /**
   * Login with credentials
   */
  const login = useCallback(async (credentials: LoginCredentials) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.login(credentials);
      setUser(response.user);
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'An unexpected error occurred during login';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Register a new account
   */
  const register = useCallback(async (data: RegisterData) => {
    setIsLoading(true);
    setError(null);

    try {
      const response = await authApi.register(data);
      setUser(response.user);
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'An unexpected error occurred during registration';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  /**
   * Logout and clear session
   */
  const logout = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      await authApi.logout();
    } catch {
      // Ignore logout errors - we'll clear local state anyway
    } finally {
      setUser(null);
      clearTokens();
      setIsLoading(false);
    }
  }, []);

  /**
   * Initialize auth state on mount
   * Attempts to restore session if token exists
   */
  useEffect(() => {
    let mounted = true;

    async function initAuth() {
      if (!hasToken()) {
        if (mounted) {
          setIsLoading(false);
        }
        return;
      }

      try {
        const userData = await fetchUser();
        if (mounted) {
          setUser(userData);
        }
      } catch {
        // Error already handled in fetchUser
      } finally {
        if (mounted) {
          setIsLoading(false);
        }
      }
    }

    initAuth();

    return () => {
      mounted = false;
    };
  }, [fetchUser]);

  // Memoize context value to prevent unnecessary re-renders
  const contextValue = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      isAuthenticated,
      isLoading,
      error,
      login,
      register,
      logout,
      refreshUser,
      clearError,
    }),
    [
      user,
      token,
      isAuthenticated,
      isLoading,
      error,
      login,
      register,
      logout,
      refreshUser,
      clearError,
    ]
  );

  return (
    <AuthContext.Provider value={contextValue}>{children}</AuthContext.Provider>
  );
}

export default AuthProvider;
